/**
 * 广告服务 Composable
 * 提供广告相关的API调用和业务逻辑
 */
import {computed, readonly, ref} from 'vue'
import axios from 'axios'

export function useAdService() {
  // 响应式状态
  const loading = ref(false)
  const error = ref(null)
  const ads = ref([])
  
  // 计算属性
  const hasAds = computed(() => ads.value.length > 0)
  const enabledAds = computed(() => ads.value.filter(ad => ad.enable))
  
  /**
   * 获取启用的广告列表
   */
  const fetchAds = async () => {
    loading.value = true
    error.value = null
    
    try {
      const response = await axios.get('/api/v1/ads', {
        timeout: 10000, // 10秒超时
        headers: {
          'Cache-Control': 'no-cache'
        }
      })
      
      if (response.data?.code === '200') {
        ads.value = response.data.data || []
        return ads.value
      } else {
        throw new Error(response.data?.info || '获取广告失败')
      }
    } catch (err) {
      const errorMsg = err.response?.data?.info || err.message || '网络请求失败'
      error.value = errorMsg
      console.error('获取广告失败:', errorMsg)
      return []
    } finally {
      loading.value = false
    }
  }
  
  /**
   * 记录广告点击
   */
  const recordAdClick = async (adId) => {
    if (!adId) {
      console.warn('广告ID为空，无法记录点击')
      return false
    }
    
    try {
      const response = await axios.post(`/api/v1/ads/${adId}/click`, null, {
        timeout: 5000 // 5秒超时
      })
      
      if (response.data?.code === '200') {
        return true
      } else {
        console.warn(`记录广告点击失败，ID: ${adId}, 原因: ${response.data?.info}`)
        return false
      }
    } catch (err) {
      console.warn(`记录广告点击失败，ID: ${adId}, 错误:`, err.message)
      return false
    }
  }
  
  /**
   * 预加载广告图片
   * @param {Array} adList 广告列表
   * @param {Object} options 配置选项
   */
  const preloadImages = (adList, options = {}) => {
    const {
      timeout = 8000,
      retryCount = 2,
      onProgress = null
    } = options
    
    if (!adList || adList.length === 0) {
      return Promise.resolve([])
    }
    
    let loadedCount = 0
    const totalCount = adList.length
    
    const loadImage = (ad, retries = retryCount) => {
      return new Promise((resolve) => {
        const img = new Image()
        
        const handleLoad = () => {
          loadedCount++
          if (onProgress) {
            onProgress(loadedCount, totalCount)
          }
          resolve(ad)
        }
        
        const handleError = () => {
          if (retries > 0) {
            setTimeout(() => {
              loadImage(ad, retries - 1).then(resolve)
            }, 1000)
          } else {
            loadedCount++
            if (onProgress) {
              onProgress(loadedCount, totalCount)
            }
            console.error(`广告图片加载失败: ${ad.url}`)
            resolve(null)
          }
        }
        
        img.onload = handleLoad
        img.onerror = handleError
        
        // 设置超时
        const timeoutId = setTimeout(() => {
          if (!img.complete) {
            img.onload = null
            img.onerror = null
            handleError()
          }
        }, timeout)
        
        img.onload = () => {
          clearTimeout(timeoutId)
          handleLoad()
        }
        
        img.src = ad.url
      })
    }
    
    return Promise.all(
      adList.map(ad => loadImage(ad))
    ).then(results => {
      return results.filter(Boolean)
    })
  }
  
  /**
   * 验证广告数据
   */
  const validateAd = (ad) => {
    if (!ad) return false
    if (!ad.url || typeof ad.url !== 'string') return false
    if (!ad.link || typeof ad.link !== 'string') return false
    return true
  }
  
  /**
   * 处理广告点击事件
   */
  const handleAdClick = async (ad) => {
    if (!validateAd(ad)) {
      console.warn('无效的广告数据:', ad)
      return
    }
    
    try {
      // 记录点击（异步，不阻塞跳转）
      recordAdClick(ad.id)
      
      // 跳转到广告链接
      if (ad.link) {
        // 检查是否为外部链接
        const isExternalLink = /^https?:\/\//.test(ad.link)
        if (isExternalLink) {
          window.open(ad.link, '_blank', 'noopener,noreferrer')
        } else {
          // 内部链接使用路由跳转
          console.log('内部链接跳转:', ad.link)
        }
      }
    } catch (err) {
      console.error('处理广告点击失败:', err)
    }
  }
  
  /**
   * 获取广告图片优化URL
   */
  const getOptimizedImageUrl = (url, options = {}) => {
    if (!url) return ''
    
    const { width, height, quality = 80, format } = options
    
    // 如果是本地图片，添加优化参数
    if (url.startsWith('/static/ad/')) {
      const params = new URLSearchParams()
      if (width) params.set('w', width)
      if (height) params.set('h', height)
      if (quality) params.set('q', quality)
      if (format) params.set('f', format)
      
      const queryString = params.toString()
      return queryString ? `${url}?${queryString}` : url
    }
    
    return url
  }
  
  /**
   * 清理资源
   */
  const cleanup = () => {
    ads.value = []
    error.value = null
    loading.value = false
  }
  
  return {
    // 状态
    loading: readonly(loading),
    error: readonly(error),
    ads: readonly(ads),
    
    // 计算属性
    hasAds,
    enabledAds,
    
    // 方法
    fetchAds,
    recordAdClick,
    preloadImages,
    validateAd,
    handleAdClick,
    getOptimizedImageUrl,
    cleanup
  }
}

/**
 * 广告配置常量
 */
export const AD_CONFIG = {
  // 自动播放间隔（毫秒）
  AUTO_PLAY_INTERVAL: 3000,
  
  // 图片加载超时（毫秒）
  IMAGE_LOAD_TIMEOUT: 8000,
  
  // 预加载重试次数
  PRELOAD_RETRY_COUNT: 2,
  
  // 缓存时长（毫秒）
  CACHE_DURATION: 5 * 60 * 1000, // 5分钟
  
  // 本地存储键名
  STORAGE_KEYS: {
    AD_CLOSED: 'ad_closed_today',
    AD_CACHE: 'ad_cache_data',
    AD_CACHE_TIME: 'ad_cache_time'
  },
  
  // 图片优化配置
  IMAGE_OPTIMIZATION: {
    DEFAULT_QUALITY: 80,
    DEFAULT_WIDTH: 800,
    SUPPORTED_FORMATS: ['webp', 'jpg', 'png']
  }
}
